import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { IconEyeOff } from "@tabler/icons-react";
import CurrentSession from "data/userSession";

// SFW mode (users_private.sfw): when on, every photo renders blurred behind a
// "Tap to reveal" overlay, so the app can be opened in public without a sudden
// NSFW image. Read the flag off the loaded session user and stay reactive to the
// events the app already fires when the user changes (settings save / sign-in).
function useSfwEnabled(): boolean {
  const [sfw, setSfw] = useState<boolean>(() => !!CurrentSession.user?.sfw);
  useEffect(() => {
    const read = () => setSfw(!!CurrentSession.user?.sfw);
    read();
    window.addEventListener("userUpdated", read);
    window.addEventListener("authStateChanged", read);
    return () => {
      window.removeEventListener("userUpdated", read);
      window.removeEventListener("authStateChanged", read);
    };
  }, []);
  return sfw;
}

// Per-image reveal state. `blurred` is true only while SFW is on AND this image
// hasn't been tapped yet; `reveal` flips it for this one image. Each image tracks
// its own reveal so revealing one doesn't reveal the rest.
export function useSfwReveal(): { blurred: boolean; reveal: () => void } {
  const sfw = useSfwEnabled();
  const [revealed, setRevealed] = useState(false);
  return { blurred: sfw && !revealed, reveal: () => setRevealed(true) };
}

// Shared overlay body: a centered chip filling its (relative) container.
function SfwOverlay({
  label,
  onReveal,
}: {
  label: string;
  onReveal?: () => void;
}) {
  return (
    <Box
      onClick={
        onReveal
          ? (e) => {
              e.stopPropagation();
              onReveal();
            }
          : undefined
      }
      onContextMenu={(e) => e.preventDefault()}
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: onReveal ? "pointer" : "default",
        zIndex: 2,
        p: 1, // keep the label off the edges so it wraps instead of clipping
        backgroundColor: "rgba(0,0,0,0.25)",
      }}
    >
      {/* A pill, not a Chip: Chip forces one line + ellipsis, so the label was
          cut off on small tiles. This wraps and stays fully legible. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          maxWidth: "100%",
          px: 1,
          py: 0.5,
          borderRadius: 2,
          backgroundColor: "rgba(0,0,0,0.6)",
          color: "#fff",
        }}
      >
        <IconEyeOff size={16} style={{ flexShrink: 0 }} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            whiteSpace: "normal",
            wordBreak: "break-word",
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

// Tap target over a blurred image. Stops propagation so revealing doesn't also
// trigger the tile's own onClick (open lightbox / expand drawer).
export function TapToReveal({ onReveal }: { onReveal: () => void }) {
  return <SfwOverlay label="Tap to reveal" onReveal={onReveal} />;
}

// Non-interactive variant for places the image can't be tapped (e.g. the tiles
// overlaid on the progress graph), so "Tap to reveal" would be a dead end.
export function SfwHidden() {
  return <SfwOverlay label="Hidden due to SFW mode" />;
}

// CSS blur applied to the image itself when blurred. `overflow: hidden` on the
// container keeps the blurred edges from bleeding past the frame.
export const SFW_BLUR_FILTER = "blur(22px)";
